import gql from 'graphql-tag';
import colorBuilder from 'color';
import {
  cloneDeep, flatten, get, isArray, isEmpty, merge, random, set,
} from 'lodash';
import moment from 'moment';
import { apolloClient } from '@/vue-apollo';

export function styleYearSegments(segment, ctx) {
  const currentYear = new Date().getFullYear();
  // eslint-disable-next-line no-underscore-dangle
  return currentYear <= ctx._proxy._rootScopes[0].data[segment.p0DataIndex].x ? [6, 6] : undefined;
}

export function groupByYear(records, entryKey) {
  return records.reduce((acc, record) => {
    if (!acc.has(record.year)) acc.set(record.year, { entries: new Set(), value: 0 });
    const yearData = acc.get(record.year);

    yearData.entries.add(get(record, entryKey));
    yearData.value += record.units;

    return acc;
  }, new Map());
}

export function groupByYearWithDetail(records, detail, entryKey) {
  return records.reduce((groups, record) => {
    let detailValues = get(record, detail);

    if (!isArray(detailValues)) detailValues = [detailValues];

    detailValues.forEach((detailValue) => {
      if (!groups.has(detailValue)) groups.set(detailValue, new Map());

      const group = groups.get(detailValue);
      if (!group.has(record.year)) group.set(record.year, { entries: new Set(), value: 0 });

      const yearData = group.get(record.year);
      yearData.entries.add(get(record, entryKey));
      yearData.value += record.units;
    });

    return groups;
  }, new Map());
}

export function computePenetrationRate(record, base) {
  return record.map((item) => {
    let denominator = base.filter((el) => (el.x === item.x));
    denominator = denominator.length === 0 ? { y: { value: 0 } } : denominator[0];
    const modifiedYObject = {
      ...item.y,
      value: denominator.y.value !== 0 ? (item.y.value / denominator.y.value) * 100 : 0,
    };

    const result = {
      ...item,
      y: modifiedYObject,
    };
    return result;
  });
}

export function convertGroupedByYearToChartData(grouped) {
  return [...grouped.keys()].sort().map((year) => {
    const data = grouped.get(year);

    return {
      x: year,
      y: {
        value: data.value,
        entries: data.entries.size,
      },
    };
  });
}

export function defaultYearSet() {
  const startYear = 2018;
  const endYear = moment().year() + 8;
  const length = endYear - startYear;
  return Array.from({ length }, (_, i) => startYear + i);
}

export function sortDataSet(data) {
  return data.sort((a, b) => {
    const itemOne = a.data.filter((year) => (year.x === moment().year()));
    const [valueOne] = itemOne;

    const itemTwo = b.data.filter((year) => (year.x === moment().year()));
    const [valueTwo] = itemTwo;

    if (valueOne?.y?.value > valueTwo?.y?.value) {
      return -1;
    }
    if (valueOne?.y?.value < valueTwo?.y?.value) {
      return 1;
    }

    return 0;
  });
}
export class ColorGenerator {
  usedFromPredefined = 0

  predefinedColors = [
    '#FE4485',
    '#ECE088',
    '#92A5EA',
    '#47C7E6',
    '#B4E43B',
    '#74F0DD',
    '#898471',
    '#D8D9CD',
    '#7CCD39',
    '#3FBBA1',
  ]

  constructor() {
    this.colorBuilder = colorBuilder;
  }

  getNextColor() {
    if (this.usedFromPredefined < this.predefinedColors.length) {
      const color = this.predefinedColors[this.usedFromPredefined];
      this.usedFromPredefined += 1;
      return color;
    }

    return this.colorBuilder.rgb(
      random(30, 230),
      random(30, 230),
      random(30, 230),
    ).string();
  }
}

function getAllMlfsOptions(mainLightFunctions) {
  return {
    ldv_light_base_functionalities: [
      ...new Set(mainLightFunctions.filter((x) => x.is_base_functionality)
        .map((x) => x.functionality)),
    ].sort(),
    ldv_light_optional_functionalities: [
      ...new Set(mainLightFunctions.filter((x) => !x.is_base_functionality)
        .map((x) => x.functionality)),
    ].sort(),
    ldv_light_functions: [...new Set(mainLightFunctions.map((x) => x.function))].sort(),
    ldv_light_sources: [...new Set(mainLightFunctions.map((x) => x.source))].sort(),
    ldv_optical_systems: [...new Set(mainLightFunctions.map((x) => x.optical_system))].sort(),
  };
}

export const TYPE = {
  line: 'line',
  bar: 'bar',
  number: 'number',
};

const priceSteps = new Array(11).fill(0).map((_, i) => i * 1000);
const priceRangeOptions = [
  ...priceSteps.map((x, i) => (i === priceSteps.length - 1
    ? `${x}+`
    : `${x} – ${priceSteps[i + 1]}`)),
];
function getPriceRange(price) {
  if (price == null) return 'Null';

  let i = 0;
  while (i <= (priceSteps.length - 1) && price >= priceSteps[i]) i += 1;

  return priceRangeOptions[i - 1];
}

export function computePenetrationRateDatasets(
  data,
  penetrationRateItems,
  getSchema,
  alreadyColoredLabels,
  entryKey,
  colorGenerator,
) {
  const options = {
    scales: {
      y: {
        stacked: true,
      },
      x: {
        stacked: true,
      },
    },
    plugins: {
      datalabels: {
        anchor: 'center',
        align: 'center',
        formatter: (item) => `${Math.round(item.y)} %`,
        font: {
          weight: 'bold',
          size: 10,
        },
      },
    },
  };

  const chartMainData = cloneDeep(data);

  const itemObject = getSchema().filter.find((item) => item.name === penetrationRateItems.field);
  const dataPath = itemObject.path;
  const recordChartMainData = chartMainData.flatMap((record) => {
    let detailValues = get(record, dataPath);

    if (!isArray(detailValues)) detailValues = [detailValues];

    if (isEmpty(detailValues)) set(record, dataPath, `No ${itemObject.label}`);
    detailValues.forEach((dtl) => {
      if (penetrationRateItems.items.includes(dtl)) {
        set(record, dataPath, dtl);
      } else {
        set(record, dataPath, 'Other');
      }
    });
    set(record, 'units', record.units / (detailValues.length !== 0 ? detailValues.length : 1));
    return record;
  });
  const yearsSet = new Set();
  defaultYearSet().forEach((item) => yearsSet.add(item));
  // recordChartMainData.forEach((item) => yearsSet.add(item.year));

  const groupedByYears = groupByYear(recordChartMainData, entryKey);
  const total = convertGroupedByYearToChartData(groupedByYears);

  const penetrationRateDatasets = flatten(
    [dataPath]
      .map(
        (path) => {
          const result = [...groupByYearWithDetail(recordChartMainData, path, entryKey)];
          return result.map(([title, values]) => {
            const color = alreadyColoredLabels[title]
              ? alreadyColoredLabels[title]
              // eslint-disable-next-line
              : alreadyColoredLabels[title] = colorGenerator.getNextColor();
            let itemData = convertGroupedByYearToChartData(values);
            // console.log('---------------')
            // console.log(itemData)
            // console.log(total)
            itemData = computePenetrationRate(itemData, total);
            // console.log(itemData)
            return {
              label: title,
              borderColor: color,
              backgroundColor: color,
              pointBackgroundColor: '#fff',
              segment: {
                borderDash: styleYearSegments,
              },
              data: itemData,
            };
          });
        },
      ),
  );

  return [
    {
      label: 'Penetration rate',
      labels: Array.from(yearsSet).sort(),
      datasets: [...sortDataSet(penetrationRateDatasets)],
      options,
      alreadyColoredLabels,
    },
  ];
}

export const sources = [
  {
    name: 'carVolumes',
    label: 'Car Volumes',
    getSchema() {
      return {
        details: [
          {
            label: 'OEM',
            value: 'car.brand.automaker.name',
          },
          {
            label: 'OEM brand',
            value: 'car.brand.name',
          },
          {
            label: 'Segment',
            value: 'car.segment',
          },
          {
            label: 'Model',
            value: 'car.model',
          },
          {
            label: 'Power train',
            value: 'car.power_train',
          },
          {
            label: 'GenPhase',
            value: 'car.generation_phase',
          },
          {
            label: 'Body',
            value: 'car.body',
          },
          {
            label: 'OEM production region',
            value: 'car.country.region',
          },
          {
            label: 'OEM production country',
            value: 'car.country.name',
          },
        ],
        split: [
          {
            label: 'OEM',
            value: 'car.brand.automaker.name',
          },
          {
            label: 'OEM brand',
            value: 'car.brand.name',
          },
          {
            label: 'Segment',
            value: 'car.segment',
          },
          {
            label: 'Model',
            value: 'car.model',
          },
          {
            label: 'Power train',
            value: 'car.power_train',
          },
          {
            label: 'GenPhase',
            value: 'car.generation_phase',
          },
          {
            label: 'Body',
            value: 'car.body',
          },
          {
            label: 'OEM production region',
            value: 'car.country.region',
          },
          {
            label: 'OEM production country',
            value: 'car.country.name',
          },
        ],
        filter: [
          {
            name: 'productionDates',
            type: 'dateRange',
            label: 'Production Dates',
            path: null,
            disable: false,
          },
          {
            name: 'oem',
            type: 'select',
            isMultiple: true,
            label: 'OEM',
            disable: false,
            path: 'car.brand.automaker.name',
          },
          {
            name: 'oemBrand',
            type: 'select',
            isMultiple: true,
            label: 'OEM brand',
            disable: false,
            path: 'car.brand.name',
          },
          {
            name: 'segment',
            type: 'select',
            isMultiple: true,
            label: 'Segment',
            disable: false,
            path: 'car.segment',
          },
          {
            name: 'model',
            type: 'select',
            isMultiple: true,
            label: 'Model',
            disable: false,
          },
          {
            name: 'powerTrain',
            type: 'select',
            isMultiple: true,
            label: 'Power train',
            disable: false,
            path: 'car.power_train',
          },
          {
            name: 'generation',
            type: 'select',
            isMultiple: true,
            label: 'Generation',
            disable: false,
            path: 'car.generation',
          },
          {
            name: 'phase',
            type: 'select',
            isMultiple: true,
            label: 'Phase',
            disable: false,
            path: 'car.phase',
          },
          {
            name: 'body',
            type: 'select',
            isMultiple: true,
            label: 'Body',
            disable: false,
            path: 'car.body',
          },
          {
            name: 'oemProductionRegion',
            type: 'select',
            isMultiple: true,
            label: 'OEM production region',
            disable: false,
            path: 'car.country.region',
          },
          {
            name: 'oemProductionCountry',
            type: 'select',
            isMultiple: true,
            label: 'OEM production country',
            disable: false,
            path: 'car.country.name',
          },
        ],
      };
    },
    getModel() {
      return {
        details: [],
        split: null,
        updateRef: null,
        filter: {
          oem: [],
          oemBrand: [],
          oemProductionRegion: [],
          oemProductionCountry: [],
          productionDates: {
            start: null,
            end: null,
          },
          segment: [],
          body: [],
          model: [],
          powerTrain: [],
          generation: [],
          phase: [],
        },
      };
    },
    async getUpdateReference(companyUuid) {
      const result = await apolloClient.query({
        query: gql`query {
          importHistory: import_history(
            where: { company_id: { _eq: "${companyUuid.replace(/-/g, '')}", }, },
            distinct_on: uuid
          ) {
            update_ref
            uuid
          }
        }`,
      });

      return {
        schema: result.data.importHistory.map((x) => ({ label: x.update_ref, value: x.uuid })),
        model: result.data.importHistory.map((x) => x.uuid)[result.data.importHistory.length - 1],
      };
    },
    async getOptions() {
      const result = await apolloClient.query({
        query: gql`query {
          oem: automakers(
            distinct_on: name,
            where: { value_chain_level: { _eq: "OEM", }, },
            order_by: {name: asc},
          ) {
            name
          }

          oemBrand: brands(
            distinct_on: name,
            where: { validated: { _eq: true }, },
            order_by: {name: asc},
          ) {
            name
          }

          oemProductionRegion: countries(
            distinct_on: region,
            order_by: {region: asc},
          ) {
            region
          }

          oemProductionCountry: countries(
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          model: cars(
            distinct_on: model,
            order_by: {model: asc},
          ) {
            model
          }

          segment: cars(
            distinct_on: segment,
            order_by: {segment: asc},
          ) {
            segment
          }

          body: cars(
            distinct_on: body,
            order_by: {body: asc},
          ) {
            body
          }

          powerTrain: cars(
            distinct_on: power_train,
            order_by: {power_train: asc},
            where: {brand: {validated: {_eq: true}}},
          ) {
            power_train
          }

          generation: cars(
            distinct_on: generation,
            order_by: {generation: asc},
          ) {
            generation
          }

          phase: cars(
            distinct_on: phase,
            order_by: {phase: asc},
          ) {
            phase
          }
        }`,
      });

      return {
        oem: result.data.oem.map((x) => x.name),
        oemBrand: result.data.oemBrand.map((x) => x.name),
        oemProductionRegion: result.data.oemProductionRegion.map((x) => x.region),
        oemProductionCountry: result.data.oemProductionCountry.map((x) => x.name),
        model: result.data.model.map((x) => x.model),
        segment: result.data.segment.map((x) => x.segment),
        body: result.data.body.map((x) => x.body),
        powerTrain: result.data.powerTrain.map((x) => x.power_train),
        generation: result.data.generation.map((x) => x.generation),
        phase: result.data.phase.map((x) => x.phase),
      };
    },
    async getDynamicOptions(model = {}, volumesData, changedField) {
      const filteredData = this.applyFilter(volumesData, model, true);
      const result = {};
      Object.keys(this.getModel().filter).map((key) => {
        if (!['productionDates'].includes(key)) {
          result[key] = [...new Set(filteredData[key])].sort();
        }
        return true;
      });

      if (changedField.field && changedField.direction === 'add') {
        delete result[changedField.field];
      }
      return result;
    },
    async getData(companyUuid, updateRefId) {
      return (await apolloClient.query({
        query: gql`query {
          volumes: volumes_${companyUuid.replace(/-/g, '')}(
              where: {_and: {units: {_is_null: false, _neq: 0}, 
              update_ref_id: {_eq: "${updateRefId}"} }}
          ) {
            id
            year
            units
            sop
            eop
            car {
              id
              sop
              eop
              model
              segment
              body
              power_train
              generation
              phase
              brand {
                name
                automaker {
                  name
                }
              }
              country {
                region
                name
              }
            }
          }
        }
        `,
      })).data.volumes
        .map((item) => merge(
          {
            car: {
              generation_phase: `G${item.car.generation}P${item.car.phase}`,
            },
          },
          cloneDeep(item),
        ));
    },
    applyFilter(data, filter, columnData = false) {
      const filteredDataInColumn = this.getModel().filter;
      const filteredData = data.filter((record) => {
        const { start, end } = filter.productionDates;
        const { sop, eop } = record;

        const startDate = new Date(start);
        const endDate = new Date(end);
        const sopDate = new Date(sop);
        const eopDate = new Date(eop);

        if (start && end) {
          if ((sopDate > endDate) || (eopDate < startDate)) return false;
        } else if (start != null) {
          if (sopDate < startDate) return false;
        } else if (end != null) {
          if (eopDate > endDate) return false;
        }

        if (filter.oem?.length > 0) {
          if (!filter.oem.includes(record.car.brand.automaker.name)) return false;
        }

        if (filter.oemBrand?.length > 0) {
          if (!filter.oemBrand.includes(record.car.brand.name)) return false;
        }

        if (filter.oemProductionRegion?.length > 0) {
          if (!filter.oemProductionRegion.includes(record.car.country.region)) return false;
        }

        if (filter.oemProductionCountry?.length > 0) {
          if (!filter.oemProductionCountry.includes(record.car.country.name)) return false;
        }

        if (filter.model?.length > 0) {
          if (!filter.model.includes(record.car.model)) return false;
        }

        if (filter.segment?.length > 0) {
          if (!filter.segment.includes(record.car.segment)) return false;
        }

        if (filter.body?.length > 0) {
          if (!filter.body.includes(record.car.body)) return false;
        }

        if (filter.powerTrain?.length > 0) {
          if (!filter.powerTrain.includes(record.car.power_train)) return false;
        }

        if (filter.generation?.length > 0) {
          if (!filter.generation.includes(record.car.generation)) return false;
        }

        if (filter.phase?.length > 0) {
          if (!filter.phase.includes(`G${record.car.generation}P${record.car.phase}`)) return false;
        }

        filteredDataInColumn.oem.push(record.car.brand.automaker.name);
        filteredDataInColumn.oemBrand.push(record.car.brand.name);
        filteredDataInColumn.oemProductionRegion.push(record.car.country.region);
        filteredDataInColumn.oemProductionCountry.push(record.car.country.name);
        filteredDataInColumn.segment.push(record.car.segment);
        filteredDataInColumn.body.push(record.car.body);
        filteredDataInColumn.model.push(record.car.model);
        filteredDataInColumn.powerTrain.push(record.car.power_train);
        filteredDataInColumn.generation.push(record.car.generation);
        filteredDataInColumn.phase.push(record.car.generation_phase);

        return true;
      });

      if (columnData) {
        return filteredDataInColumn;
      }
      return filteredData;
    },
    getCharts(data, details = [], splitBy = null, stacked = false, penetrationRateItems = null) {
      const entryKey = 'car.id';
      let options = null;
      if (stacked) {
        options = {
          scales: {
            y: {
              stacked: true,
            },
            x: {
              stacked: true,
            },
          },
          plugins: {
            datalabels: {
              anchor: 'center',
              align: 'center',
              // eslint-disable-next-line
              formatter: function (item) {
                return '';
              },
              font: {
                weight: 'bold',
                size: 10,
              },
            },
          },
        };
      }

      if (splitBy == null) {
        const colorGenerator = new ColorGenerator();

        if (penetrationRateItems !== null) {
          return computePenetrationRateDatasets(
            data,
            penetrationRateItems,
            this.getSchema,
            {},
            entryKey,
            colorGenerator,
          );
        }

        const groupedByYears = groupByYear(data, entryKey);

        let detailsDatasets = flatten(
          details
            .map(
              (detail) => {
                const result = [...groupByYearWithDetail(data, detail, entryKey)];
                return result.map(([title, values]) => {
                  const color = colorGenerator.getNextColor();

                  return {
                    label: title,
                    borderColor: color,
                    backgroundColor: color,
                    pointBackgroundColor: '#fff',
                    segment: {
                      borderDash: styleYearSegments,
                    },
                    data: convertGroupedByYearToChartData(values),
                  };
                });
              },
            ),
        );

        if (stacked) {
          detailsDatasets = sortDataSet(detailsDatasets);
        }

        const yearsSet = new Set();
        defaultYearSet().forEach((item) => yearsSet.add(item));
        // data.forEach((item) => yearsSet.add(item.year));
        return [
          {
            label: 'All cars',
            labels: Array.from(yearsSet).sort(),
            datasets: [
              {
                label: 'Total',
                borderColor: '#FFA829',
                backgroundColor: '#FFA829',
                pointBackgroundColor: '#fff',
                segment: {
                  borderDash: styleYearSegments,
                },
                data: convertGroupedByYearToChartData(groupedByYears),
              },
              ...detailsDatasets,
            ],
            options,
          },
        ];
      }

      const splitVolumes = data.reduce((groups, record) => {
        let detailValues = get(record, splitBy);

        if (!isArray(detailValues)) detailValues = [detailValues];

        detailValues.forEach((detailValue) => {
          if (!groups.has(detailValue)) groups.set(detailValue, []);

          const group = groups.get(detailValue);
          group.push(record);
        });

        return groups;
      }, new Map());

      const colorGenerator = new ColorGenerator();
      let alreadyColoredLabels = {};
      return [...splitVolumes].sort(([a], [b]) => a.toString().localeCompare(b)).map(([label, volumes]) => {
        if (penetrationRateItems !== null) {
          let result = computePenetrationRateDatasets(
            volumes,
            penetrationRateItems,
            this.getSchema,
            alreadyColoredLabels,
            entryKey,
            colorGenerator,
          );
          [result] = result;
          alreadyColoredLabels = {
            ...result.alreadyColoredLabels,
          };
          result = {
            ...result,
            label,
          };
          return result;
        }
        const groupedByYears = groupByYear(volumes, entryKey);

        let detailsDatasets = flatten(
          details
            .map(
              (detail) => (
                [...groupByYearWithDetail(volumes, detail, entryKey)].map(([title, values]) => {
                  const color = alreadyColoredLabels[title]
                    ? alreadyColoredLabels[title]
                    : alreadyColoredLabels[title] = colorGenerator.getNextColor();

                  return {
                    label: title,
                    borderColor: color,
                    backgroundColor: color,
                    pointBackgroundColor: '#fff',
                    segment: {
                      borderDash: styleYearSegments,
                    },
                    data: convertGroupedByYearToChartData(values),
                  };
                })
              ),
            ),
        );

        if (stacked) {
          detailsDatasets = sortDataSet(detailsDatasets);
        }

        const yearsSet = new Set();

        defaultYearSet().forEach((item) => yearsSet.add(item));

        return {
          label,
          labels: Array.from(yearsSet).sort(),
          datasets: [
            {
              label: 'Total',
              borderColor: '#FFA829',
              backgroundColor: '#FFA829',
              pointBackgroundColor: '#fff',
              segment: {
                borderDash: styleYearSegments,
              },
              data: convertGroupedByYearToChartData(groupedByYears),
            },
            ...detailsDatasets,
          ],
          options,
        };
      });
    },
  },
  {
    label: 'Lighting Device Volumes',
    name: 'lightingDeviceVolumes',
    getSchema() {
      return {
        details: [
          {
            label: 'Type',
            value: 'ldv_type',
          },
          {
            label: 'Supplier',
            value: 'ldv_supplier_name',
          },
          {
            label: 'Supplier production region',
            value: 'ldv_supplier_production_region',
          },
          {
            label: 'Supplier production country',
            value: 'ldv_supplier_production_country',
          },
          {
            label: 'Complexity',
            value: 'ldv_complexity',
          },
          {
            label: 'Aftermarket price',
            value: 'ldv_aftermarket_price_range',
          },
          {
            label: 'Base functionality',
            value: 'ldv_light_base_functionalities',
          },
          {
            label: 'Optional functionality',
            value: 'ldv_light_optional_functionalities',
          },
          {
            label: 'Function',
            value: 'ldv_light_functions',
          },
          {
            label: 'Light source technology',
            value: 'ldv_light_sources',
          },
          {
            label: 'Optical concept',
            value: 'ldv_optical_systems',
          },
          {
            label: 'OEM',
            value: 'car_automaker_name',
          },
          {
            label: 'OEM brand',
            value: 'car_brand_name',
          },
          {
            label: 'Segment',
            value: 'car_segment',
          },
          {
            label: 'Model',
            value: 'car_model',
          },
          {
            label: 'Power train',
            value: 'car_power_train',
          },
          {
            label: 'GenPhase',
            value: 'car_generation_phase',
          },
          {
            label: 'Body',
            value: 'car_body',
          },
          {
            label: 'OEM production region',
            value: 'car_production_region',
          },
          {
            label: 'OEM production country',
            value: 'car_production_country',
          },
        ],
        split: [
          {
            label: 'Type',
            value: 'ldv_type',
          },
          {
            label: 'Supplier',
            value: 'ldv_supplier_name',
          },
          {
            label: 'Supplier production region',
            value: 'ldv_supplier_production_region',
          },
          {
            label: 'Supplier production country',
            value: 'ldv_supplier_production_country',
          },
          {
            label: 'Complexity',
            value: 'ldv_complexity',
          },
          {
            label: 'Aftermarket price',
            value: 'ldv_aftermarket_price_range',
          },
          {
            label: ' Base functionality',
            value: 'ldv_light_base_functionalities',
          },
          {
            label: 'Optional functionality',
            value: 'ldv_light_optinal_functionalities',
          },
          {
            label: 'Function',
            value: 'ldv_light_functions',
          },
          {
            label: 'Light source technology',
            value: 'ldv_light_sources',
          },
          {
            label: 'Optical concept',
            value: 'ldv_optical_systems',
          },
          {
            label: 'OEM',
            value: 'car_automaker_name',
          },
          {
            label: 'OEM brand',
            value: 'car_brand_name',
          },
          {
            label: 'OEM production region',
            value: 'car_production_region',
          },
          {
            label: 'OEM production country',
            value: 'car_production_country',
          },
          {
            label: 'Model',
            value: 'car_model',
          },
          {
            label: 'Segment',
            value: 'car_segment',
          },
          {
            label: 'Body',
            value: 'car_body',
          },
          {
            label: 'Power train',
            value: 'car_power_train',
          },
          {
            label: 'GenPhase',
            value: 'car_generation_phase',
          },
        ],
        filter: [
          {
            name: 'carProductionDates',
            type: 'dateRange',
            label: 'Production Dates',
            columnId: 'car',
            disable: false,
            path: null,
          },
          {
            name: 'carOem',
            type: 'select',
            isMultiple: true,
            label: 'OEM',
            columnId: 'car',
            disable: false,
            path: 'car_automaker_name',
          },
          {
            name: 'carOemBrand',
            type: 'select',
            isMultiple: true,
            label: 'OEM Brand',
            columnId: 'car',
            disable: false,
            path: 'car_brand_name',
          },
          {
            name: 'carOemProductionRegion',
            type: 'select',
            isMultiple: true,
            label: 'OEM production region',
            columnId: 'car',
            disable: false,
            path: 'car_production_region',
          },
          {
            name: 'carOemProductionCountry',
            type: 'select',
            isMultiple: true,
            label: 'OEM production country',
            columnId: 'car',
            disable: false,
            path: 'car_production_name',
          },
          {
            name: 'carSegment',
            type: 'select',
            isMultiple: true,
            label: 'Segment',
            columnId: 'car',
            disable: false,
            path: 'car_segment',
          },
          {
            name: 'carBody',
            type: 'select',
            isMultiple: true,
            label: 'Body',
            columnId: 'car',
            disable: false,
            path: 'car_body',
          },
          {
            name: 'carModel',
            type: 'select',
            isMultiple: true,
            label: 'Model',
            columnId: 'car',
            disable: false,
            path: 'car_model',
          },
          {
            name: 'carPowerTrain',
            type: 'select',
            isMultiple: true,
            label: 'Power train',
            columnId: 'car',
            disable: false,
            path: 'car_power_train',
          },
          {
            name: 'carGeneration',
            type: 'select',
            isMultiple: true,
            label: 'Generation',
            columnId: 'car',
            disable: false,
            path: 'car_generation',
          },
          {
            name: 'carPhase',
            type: 'select',
            isMultiple: true,
            label: 'Phase',
            columnId: 'car',
            disable: false,
            path: 'car_phase',
          },
          {
            name: 'type',
            type: 'select',
            isMultiple: true,
            label: 'Type',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_type',
          },
          {
            name: 'supplier',
            type: 'select',
            isMultiple: true,
            label: 'Supplier',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_supplier_name',
          },
          {
            name: 'supplierProductionRegion',
            type: 'select',
            isMultiple: true,
            label: 'Supplier production region',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_supplier_production_region',
          },
          {
            name: 'supplierProductionCountry',
            type: 'select',
            isMultiple: true,
            label: 'Supplier production country',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_supplier_production_country',
          },
          {
            name: 'complexity',
            type: 'select',
            isMultiple: true,
            label: 'Complexity',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_complexity',
          },
          {
            name: 'aftermarket_price',
            type: 'select',
            isMultiple: true,
            label: 'Aftermarket price',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_aftermarket_price_range',
          },
          {
            name: 'baseFunctionality',
            type: 'select',
            isMultiple: true,
            label: 'Base functionality',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_light_base_functionalities',
          },
          {
            name: 'optionalFunctionality',
            type: 'select',
            isMultiple: true,
            label: 'Optional functionality',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_light_optional_functionalities',
          },
          {
            name: 'function',
            type: 'select',
            isMultiple: true,
            label: 'Function',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_light_functions',
          },
          {
            name: 'lightSourceTechnology',
            type: 'select',
            isMultiple: true,
            label: 'Light source technology',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_light_sources',
          },
          {
            name: 'opticalSystem',
            type: 'select',
            isMultiple: true,
            label: 'Optical concept',
            columnId: 'lightingDevice',
            disable: false,
            path: 'ldv_optical_systems',
          },
        ],
      };
    },
    getModel() {
      return {
        details: [],
        filter: {
          carProductionDates: {
            start: null,
            end: null,
          },
          carOem: [],
          carOemBrand: [],
          carOemProductionRegion: [],
          carOemProductionCountry: [],
          carSegment: [],
          carBody: [],
          carModel: [],
          carPowerTrain: [],
          carGeneration: [],
          carPhase: [],
          type: [],
          supplier: [],
          factory: [],
          supplierProductionRegion: [],
          supplierProductionCountry: [],
          complexity: [],
          aftermarket_price: [],
          baseFunctionality: [],
          optionalFunctionality: [],
          function: [],
          lightSourceTechnology: [],
          opticalSystem: [],
        },
      };
    },
    async getOptions() {
      const result = await apolloClient.query({
        query: gql`query {
          carOem: automakers(
            where: { value_chain_level: { _eq: "OEM", }, },
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          carOemBrand: brands(
            where: { validated: { _eq: true }, },
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          carOemProductionRegion: countries(
            distinct_on: region,
            order_by: {region: asc},
          ) {
            region
          }

          carOemProductionCountry: countries(
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          carSegment: cars(
            distinct_on: segment,
            order_by: {segment: asc},
          ) {
            segment
          }

          carBody: cars(
            distinct_on: body,
            order_by: {body: asc},
          ) {
            body
          }

          carModel: cars(
            distinct_on: model,
            order_by: {model: asc},
          ) {
            model
          }

          carPowerTrain: cars(
            distinct_on: power_train,
            order_by: {power_train: asc},
            where: {brand: {validated: {_eq: true}}},
          ) {
            power_train
          }

          carGeneration: cars(
            distinct_on: generation,
            order_by: {generation: asc},
          ) {
            generation
          }

          carPhase: cars(
            distinct_on: phase,
            order_by: {phase: asc},
          ) {
            phase
          }

          supplier: brands(
            where: { value_chain_level: { _eq: "Tier1", }, },
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          factory: factories(
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          supplierProductionRegion: countries(
            distinct_on: region,
            order_by: {region: asc},
          ) {
            region
          }

          supplierProductionCountry: countries(
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          complexity: lighting_devices(
            distinct_on: complexity,
            order_by: {complexity: asc},
          ) {
            complexity
          }

          type: lighting_devices(
            distinct_on: type,
            where: {type: {_nin: ["CHMSL","AuxHL","AuxRL"]}},
            order_by: {type: asc},
          ) {
            type
          }

          functionality: light_functionalities(
            order_by: {name: asc},
          ) {
            name
            is_base_functionality
          }

          function: light_functions(
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }

          lightSourceTechnology: light_sources(
            distinct_on: technology,
            order_by: {technology: asc},
          ) {
            technology
          }

          opticalSystem: optical_systems(
            distinct_on: name,
            order_by: {name: asc},
          ) {
            name
          }
        }`,
      });

      return {
        carOem: result.data.carOem.map((x) => x.name),
        carOemBrand: result.data.carOemBrand.map((x) => x.name),
        carOemProductionRegion: result.data.carOemProductionRegion.map((x) => x.region),
        carOemProductionCountry: result.data.carOemProductionCountry.map((x) => x.name),
        carSegment: result.data.carSegment.map((x) => x.segment),
        carBody: result.data.carBody.map((x) => x.body),
        carModel: result.data.carModel.map((x) => x.model),
        carPowerTrain: result.data.carPowerTrain.map((x) => x.power_train),
        carGeneration: result.data.carGeneration.map((x) => x.generation),
        carPhase: result.data.carPhase.map((x) => x.phase),
        type: result.data.type.map((x) => x.type),
        supplier: result.data.supplier.map((x) => x.name),
        factory: result.data.factory.map((x) => x.name),
        supplierProductionRegion: result.data.supplierProductionRegion.map((x) => x.region),
        supplierProductionCountry: result.data.supplierProductionCountry.map((x) => x.name),
        complexity: result.data.complexity.map((x) => x.complexity),
        aftermarket_price: priceRangeOptions,
        baseFunctionality: result.data.functionality.filter((x) => x.is_base_functionality).map((x) => x.name),
        optionalFunctionality: result.data.functionality.filter((x) => !x.is_base_functionality).map((x) => x.name),
        function: result.data.function.map((x) => x.name),
        lightSourceTechnology: result.data.lightSourceTechnology.map((x) => x.technology),
        opticalSystem: result.data.opticalSystem.map((x) => x.name),
      };
    },
    async getUpdateReference(companyUuid) {
      const result = await apolloClient.query({
        query: gql`query {
          importHistory: import_history(
            where: { company_id: { _eq: "${companyUuid.replace(/-/g, '')}", }, },
            distinct_on: uuid
          ) {
            update_ref
            uuid
          }
        }`,
      });

      return {
        schema: result.data.importHistory.map((x) => ({ label: x.update_ref, value: x.uuid })),
        model: result.data.importHistory.map((x) => x.uuid)[result.data.importHistory.length - 1],
      };
    },
    async getDynamicOptions(model = {}, volumesData, changedField) {
      const filteredData = this.applyFilter(volumesData, model, true);
      const result = {};
      Object.keys(this.getModel().filter).map((key) => {
        if (!['carProductionDates'].includes(key)) {
          result[key] = [...new Set(filteredData[key])].sort();
        }
        return true;
      });

      if (changedField.field && changedField.direction === 'add') {
        delete result[changedField.field];
      }
      return result;
    },
    async getData(companyUuid, updateRefId) {
      const result = (await apolloClient.query({
        query: gql`query {
          third_party_take_rates_aggregated(
            where: {
                  _and: {
                    volumes_${companyUuid.replace(/-/g, '')}: {_is_null: false, _neq: 0},
                    update_ref_id_${companyUuid.replace(/-/g, '')}: {_eq: "${updateRefId}"}
                  }
            }
            distinct_on: [
              rate,
              parts_per_car,
              year,
              car_automaker_name,
              car_model,
              car_brand_name,
              car_body,
              car_factory_country,
              car_factory_region,
              car_production_country,
              car_production_region,
              car_generation,
              car_phase,
              car_power_train,
              car_segment,
              ldv_id,
              ldv_aftermarket_price,
              ldv_complexity,
              ldv_factory_name,
              ldv_supplier_production_country,
              ldv_supplier_production_region,
              ldv_main_light_functions_str,
              ldv_supplier_name,
              ldv_type,
              update_ref_id_${companyUuid.replace(/-/g, '')}
            ]
          ) {
            sources_units: volumes_${companyUuid.replace(/-/g, '')}
            rate
            parts_per_car
            year
            car_automaker_name
            car_model
            car_brand_name
            car_body
            car_eop: eop_${companyUuid.replace(/-/g, '')}
            car_sop: sop_${companyUuid.replace(/-/g, '')}
            car_factory_country
            car_factory_region
            car_production_country
            car_production_region
            car_generation
            car_phase
            car_power_train
            car_segment
            ldv_id
            ldv_aftermarket_price
            ldv_complexity
            ldv_factory_name
            ldv_supplier_production_country
            ldv_supplier_production_region
            ldv_main_light_functions
            ldv_supplier_name
            ldv_type
          }
        }`,
      }));
      return result.data.third_party_take_rates_aggregated
        .map((item) => ({
          car_generation_phase: `\
G${item.car_generation}P${item.car_phase}`,
          ldv_aftermarket_price_range: getPriceRange(item.ldv_aftermarket_price),
          ...getAllMlfsOptions(item.ldv_main_light_functions),
          ...item,
          // eslint-disable-next-line
          units: Math.round((item.sources_units ? item.sources_units : null) * (item.rate ? item.rate : 0) * (item.parts_per_car ? item.parts_per_car : 0)),
        }));
    },
    applyFilter(data, filter, columnData = false) {
      const schema = {
        carOem: 'car_automaker_name',
        carOemBrand: 'car_brand_name',
        carOemProductionRegion: 'car_production_region',
        carOemProductionCountry: 'car_production_country',
        carSegment: 'car_segment',
        carBody: 'car_body',
        carModel: 'car_model',
        carPowerTrain: 'car_power_train',
        carGeneration: 'car_generation',
        carPhase: 'car_phase',
        type: 'ldv_type',
        supplier: 'ldv_supplier_name',
        factory: 'ldv_factory_name',
        supplierProductionRegion: 'ldv_supplier_production_region',
        supplierProductionCountry: 'ldv_supplier_production_country',
        complexity: 'ldv_complexity',
        aftermarket_price: 'ldv_aftermarket_price_range',
        baseFunctionality: 'ldv_light_base_functionalities',
        optionalFunctionality: 'ldv_light_optional_functionalities',
        function: 'ldv_light_functions',
        lightSourceTechnology: 'ldv_light_sources',
        opticalSystem: 'ldv_optical_systems',
      };

      const filteredDataInColumn = this.getModel().filter;
      const filteredData = data.filter((record) => {
        const { start, end } = filter.carProductionDates;
        const sop = record.car_sop;
        const eop = record.car_eop;

        const startDate = new Date(start);
        const endDate = new Date(end);
        const sopDate = new Date(sop);
        const eopDate = new Date(eop);

        if (start && end) {
          if (sopDate > endDate || eopDate < startDate) return false;
        } else if (start != null) {
          if (sopDate < startDate) return false;
        } else if (end != null) {
          if (eopDate > endDate) return false;
        }

        if (filter.carOem?.length > 0) {
          if (!filter.carOem.includes(record.car_automaker_name)) return false;
        }

        if (filter.carOemBrand?.length > 0) {
          if (!filter.carOemBrand.includes(record.car_brand_name)) return false;
        }

        if (filter.carOemProductionRegion?.length > 0) {
          if (
            !filter.carOemProductionRegion.includes(record.car_production_region)
          ) return false;
        }

        if (filter.carOemProductionCountry?.length > 0) {
          if (
            !filter.carOemProductionCountry.includes(record.car_production_country)
          ) return false;
        }

        if (filter.carSegment?.length > 0) {
          if (!filter.carSegment.includes(record.car_segment)) return false;
        }

        if (filter.carBody?.length > 0) {
          if (!filter.carBody.includes(record.car_body)) return false;
        }

        if (filter.carModel?.length > 0) {
          if (!filter.carModel.includes(record.car_model)) return false;
        }

        if (filter.carPowerTrain?.length > 0) {
          if (!filter.carPowerTrain.includes(record.car_power_train)) return false;
        }

        if (filter.carGeneration?.length > 0) {
          if (!filter.carGeneration.includes(record.car_generation)) return false;
        }

        if (filter.carPhase?.length > 0) {
          if (!filter.carPhase.includes(record.car_phase)) return false;
        }

        if (filter.type?.length > 0) {
          if (!filter.type.includes(record.ldv_type)) return false;
        }

        if (filter.supplier?.length > 0) {
          if (!filter.supplier.includes(record.ldv_supplier_name)) return false;
        }

        if (filter.factory?.length > 0) {
          if (!filter.factory.includes(record.ldv_factory_name)) return false;
        }

        if (filter.supplierProductionRegion?.length > 0) {
          if (
            !filter.supplierProductionRegion.includes(record.ldv_supplier_production_region)
          ) return false;
        }

        if (filter.supplierProductionCountry?.length > 0) {
          if (
            !filter.supplierProductionCountry.includes(record.ldv_supplier_production_country)
          ) return false;
        }

        if (filter.complexity?.length > 0) {
          if (
            !filter.complexity.includes(record.ldv_complexity)
          ) return false;
        }

        if (filter.aftermarket_price?.length > 0) {
          if (
            !filter.aftermarket_price.includes(record.ldv_aftermarket_price_range)
          ) return false;
        }

        if (
          filter.baseFunctionality?.length > 0
          || filter.optionalFunctionality?.length > 0
          || filter.function?.length > 0
          || filter.lightSourceTechnology?.length > 0
          || filter.opticalSystem?.length > 0
        ) {
          const suitableMlfs = record.ldv_main_light_functions.filter((mlf) => {
            if (filter.baseFunctionality?.length > 0) {
              if (!(filter.baseFunctionality.includes(mlf.functionality) && mlf.is_base_functionality)) return false;
            }

            if (filter.optionalFunctionality?.length > 0) {
              if (!(filter.optionalFunctionality.includes(mlf.functionality) && !mlf.is_base_functionality)) {
                return false;
              }
            }

            if (filter.function?.length > 0) {
              if (!filter.function.includes(mlf.function)) return false;
            }

            if (filter.lightSourceTechnology?.length > 0) {
              if (!filter.lightSourceTechnology.includes(mlf.source)) return false;
            }

            if (filter.opticalSystem?.length > 0) {
              if (!filter.opticalSystem.includes(mlf.optical_system)) return false;
            }

            return true;
          });

          if (suitableMlfs.length === 0) return false;

          Object.assign(record, getAllMlfsOptions(suitableMlfs));
        }

        Object.keys(schema).map((item) => {
          if (Array.isArray(record[schema[item]])) {
            filteredDataInColumn[item].push(...record[schema[item]]);
          } else {
            filteredDataInColumn[item].push(record[schema[item]]);
          }
          return true;
        });

        return true;
      });

      if (columnData) {
        return filteredDataInColumn;
      }
      return filteredData;
    },
    getCharts(data, details = [], splitBy = null, stacked = false, penetrationRateItems = null) {
      const entryKey = 'ldv_id';
      let options = null;
      if (stacked) {
        options = {
          scales: {
            y: {
              stacked: true,
            },
            x: {
              stacked: true,
            },
          },
          plugins: {
            datalabels: {
              anchor: 'center',
              align: 'center',
              // eslint-disable-next-line
              formatter: function (item) {
                return item.y;
              },
              font: {
                weight: 'bold',
                size: 10,
              },
            },
          },
        };
      }

      if (splitBy == null) {
        const colorGenerator = new ColorGenerator();

        if (penetrationRateItems !== null) {
          return computePenetrationRateDatasets(
            data,
            penetrationRateItems,
            this.getSchema,
            {},
            entryKey,
            colorGenerator,
          );
        }

        const groupedByYears = groupByYear(data, entryKey);

        let detailsDatasets = flatten(
          details
            .map(
              (detail) => (
                [...groupByYearWithDetail(data, detail, entryKey)].map(([title, values]) => {
                  const color = colorGenerator.getNextColor();

                  return {
                    label: title,
                    borderColor: color,
                    backgroundColor: color,
                    pointBackgroundColor: '#fff',
                    segment: {
                      borderDash: styleYearSegments,
                    },
                    data: convertGroupedByYearToChartData(values),
                  };
                })
              ),
            ),
        );

        if (stacked) {
          detailsDatasets = sortDataSet(detailsDatasets);
        }

        const yearsSet = new Set();

        defaultYearSet().forEach((item) => yearsSet.add(item));
        // data.forEach((item) => yearsSet.add(item.year));

        return [
          {
            label: 'All Lighting Devices',
            labels: Array.from(yearsSet).sort(),
            datasets: [
              {
                label: 'Total',
                borderColor: '#FFA829',
                backgroundColor: '#FFA829',
                pointBackgroundColor: '#fff',
                segment: {
                  borderDash: styleYearSegments,
                },
                data: convertGroupedByYearToChartData(groupedByYears),
              },
              ...detailsDatasets,
            ],
            options,
          },
        ];
      }

      const splitVolumes = data.reduce((groups, record) => {
        let detailValues = get(record, splitBy);

        if (!isArray(detailValues)) detailValues = [detailValues];

        detailValues.forEach((detailValue) => {
          if (!groups.has(detailValue)) groups.set(detailValue, []);

          const group = groups.get(detailValue);
          group.push(record);
        });

        return groups;
      }, new Map());

      const colorGenerator = new ColorGenerator();
      let alreadyColoredLabels = {};
      return [...splitVolumes].map((item) => {
        const splitVolume = item;
        splitVolume[0] = splitVolume[0] === null ? 'None' : splitVolume[0];
        return splitVolume;
      })
        .sort(([a], [b]) => a.toString().localeCompare(b)).map(([label, volumes]) => {
          if (penetrationRateItems !== null) {
            let result = computePenetrationRateDatasets(
              volumes,
              penetrationRateItems,
              this.getSchema,
              alreadyColoredLabels,
              entryKey,
              colorGenerator,
            );
            [result] = result;
            alreadyColoredLabels = {
              ...result.alreadyColoredLabels,
            };
            result = {
              ...result,
              label,
            };
            return result;
          }

          const groupedByYears = groupByYear(volumes, entryKey);

          let detailsDatasets = flatten(
            details
              .map(
                (detail) => (
                  [...groupByYearWithDetail(volumes, detail, entryKey)].map(([title, values]) => {
                    const color = alreadyColoredLabels[title]
                      ? alreadyColoredLabels[title]
                      : alreadyColoredLabels[title] = colorGenerator.getNextColor();

                    return {
                      label: title,
                      borderColor: color,
                      backgroundColor: color,
                      pointBackgroundColor: '#fff',
                      segment: {
                        borderDash: styleYearSegments,
                      },
                      data: convertGroupedByYearToChartData(values),
                    };
                  })
                ),
              ),
          );

          if (stacked) {
            detailsDatasets = sortDataSet(detailsDatasets);
          }

          const yearsSet = new Set();
          defaultYearSet().forEach((item) => yearsSet.add(item));
          // volumes.forEach((item) => yearsSet.add(item.year));

          return {
            label,
            labels: Array.from(yearsSet).sort(),
            datasets: [
              {
                label: 'Total',
                borderColor: '#FFA829',
                backgroundColor: '#FFA829',
                pointBackgroundColor: '#fff',
                segment: {
                  borderDash: styleYearSegments,
                },
                data: convertGroupedByYearToChartData(groupedByYears),
              },
              ...detailsDatasets,
            ],
            options,
          };
        });
    },
  },
];

export const sourcesMap = sources.reduce((acc, source) => ({
  ...acc,
  [source.name]: source,
}), {});
